import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProduct } from '../../context/ProductContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { MoneyInput } from '../../components/ui/MoneyInput'
import { Button } from '../../components/ui/Button'
import * as categoryService from '../../services/categoryService'
import { ImageUploadField } from '../../components/ui/ImageUploadField'
import { ProductImageGalleryEditor } from '../../components/ui/ProductImageGalleryEditor'
import { CategorySearchSelect } from '../../components/ui/CategorySearchSelect'

const fieldAreaClass =
  'w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 min-h-[96px]'

const STEPS = ['Basics', 'Pricing & stock', 'Description & images'] as const
const LAST_STEP = STEPS.length - 1

export default function ProductCreate() {
  const navigate = useNavigate()
  const { createProduct, mutationLoading, error, clearError } = useProduct()
  const [categories, setCategories] = useState<categoryService.CategoryRow[]>([])
  const [imageUploading, setImageUploading] = useState(false)
  const [step, setStep] = useState(0)
  const [form, setForm] = useState({
    name: '',
    sku: '',
    price: '',
    stockQuantity: '0',
    description: '',
    otherDetails: '',
    categorySlug: '',
    imageUrls: [] as string[],
  })

  useEffect(() => {
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((d) => setCategories(d.items || []))
      .catch(() => setCategories([]))
  }, [])

  /** Only real `store_categories` rows can be assigned; orphan slugs from products are not valid for POST. */
  const assignableCategories = categories.filter((c) => c.id != null && c.source === 'store')

  function removeImage(url: string) {
    setForm((f) => ({ ...f, imageUrls: f.imageUrls.filter((u) => u !== url) }))
  }

  const basicsOk = form.name.trim().length > 0
  const pricingOk =
    form.price !== '' && !Number.isNaN(Number(form.price)) && Number(form.price) >= 0
  const stockOk = form.stockQuantity !== '' && !Number.isNaN(Number(form.stockQuantity)) && Number(form.stockQuantity) >= 0

  function canGoNext(): boolean {
    if (step === 0) return basicsOk
    if (step === 1) return pricingOk && stockOk
    if (step === 2) return basicsOk && pricingOk && stockOk
    return true
  }

  async function submit(isActive: boolean) {
    clearError()
    const payload = {
      name: form.name.trim(),
      ...(form.sku.trim() ? { sku: form.sku.trim() } : {}),
      price: Number(form.price),
      stockQuantity: Number(form.stockQuantity),
      description: form.description.trim() || undefined,
      otherDetails: form.otherDetails.trim() || undefined,
      isActive,
      categorySlug: form.categorySlug.trim() || undefined,
      images: form.imageUrls.length ? form.imageUrls : undefined,
    }
    try {
      const created = await createProduct(payload)
      navigate(`/products/${created.id}`)
    } catch {
      /* error surfaced in context */
    }
  }

  return (
    <Card className="w-full max-w-none">
      <CardHeader
        title="Add product"
        description="Step through details, then save as draft or publish to your storefront. The URL slug is generated automatically on save."
      />

      <nav
        className="mb-8 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Form steps"
      >
        <ol className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3 lg:flex-nowrap lg:gap-4">
          {STEPS.map((label, i) => (
            <li key={label} className="flex min-w-0 items-center gap-2 sm:gap-2.5">
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                  i === step
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary/30 ring-offset-2 ring-offset-card'
                    : i < step
                      ? 'bg-primary/15 text-primary hover:bg-primary/20'
                      : 'bg-muted text-muted-foreground'
                } ${i < step ? 'cursor-pointer' : i > step ? 'cursor-default' : ''}`}
              >
                {i + 1}
              </button>
              <span
                className={`hidden text-sm sm:inline ${i === step ? 'font-medium text-foreground' : 'text-muted-foreground'}`}
              >
                {label}
              </span>
              {i < STEPS.length - 1 ? (
                <span className="mx-0.5 text-muted-foreground/50 sm:mx-1" aria-hidden>
                  /
                </span>
              ) : null}
            </li>
          ))}
        </ol>
        <p className="mt-3 text-sm font-medium text-foreground sm:hidden">
          Step {step + 1} of {STEPS.length}: {STEPS[step]}
        </p>
      </nav>

      <div className="w-full min-w-0 space-y-6 lg:space-y-8">
        {step === 0 ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
            <Input
              id="name"
              label="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="min-w-0 lg:col-span-1"
            />
            <Input
              id="sku"
              label="SKU"
              value={form.sku}
              onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="Optional — auto-generated from product name if blank"
              className="min-w-0 lg:col-span-1"
            />
            <div className="min-w-0 lg:col-span-2">
              <label htmlFor="categorySlug" className="mb-2 block text-sm font-medium text-foreground">
                Category
              </label>
              <CategorySearchSelect
                id="categorySlug"
                value={
                  assignableCategories.some((c) => c.slug === form.categorySlug) ? form.categorySlug : ''
                }
                onValueChange={(slug) => setForm({ ...form, categorySlug: slug })}
                categories={assignableCategories}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Choose a category you created under <strong>Categories</strong>, or leave as no category. Free-text
                slugs are not accepted.
              </p>
              {assignableCategories.length === 0 ? (
                <p className="mt-2 rounded-xl border border-[color-mix(in_oklch,var(--warning)_35%,transparent)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] px-3 py-2 text-xs text-foreground">
                  No store categories yet. Add one under Categories, or save this product without a category.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="grid grid-cols-1 gap-4 sm:max-w-2xl sm:gap-5 lg:max-w-none lg:grid-cols-2 lg:gap-x-8">
            <MoneyInput
              id="price"
              label="Price *"
              value={form.price}
              onChange={(price) => setForm({ ...form, price })}
              required
              className="min-w-0"
            />
            <Input
              id="stockQuantity"
              label="Stock quantity *"
              type="number"
              min="0"
              value={form.stockQuantity}
              onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
              required
              className="min-w-0"
            />
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-5 xl:gap-8">
            <div className="min-w-0 xl:col-span-3 space-y-5">
              <div>
                <label htmlFor="description" className="mb-2 block text-sm font-medium text-foreground">
                  Description
                </label>
                <textarea
                  id="description"
                  className={fieldAreaClass}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={6}
                />
              </div>
              <div>
                <label htmlFor="otherDetails" className="mb-2 block text-sm font-medium text-foreground">
                  Other details <span className="font-normal text-muted-foreground">(optional)</span>
                </label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Shown on the storefront as an &quot;Other details&quot; tab — specs, care, ingredients, dimensions,
                  warranty, etc.
                </p>
                <textarea
                  id="otherDetails"
                  className={fieldAreaClass}
                  value={form.otherDetails}
                  onChange={(e) => setForm({ ...form, otherDetails: e.target.value })}
                  rows={5}
                  placeholder="e.g. Materials, country of origin, care instructions…"
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-5 xl:col-span-2">
              <span className="text-sm font-medium text-foreground">Images</span>
              <ProductImageGalleryEditor
                urls={form.imageUrls}
                onRemove={removeImage}
                caption="Preview — order is used on the storefront. Remove or add more below."
              />
              <ImageUploadField
                label={form.imageUrls.length ? 'Add more images' : 'Upload images'}
                hint="PNG, JPEG, WebP, or GIF. You can add several; order is preserved."
                multiple
                disabled={mutationLoading}
                onUploadStateChange={setImageUploading}
                onUploadError={(msg) => window.alert(msg)}
                onUploaded={(urls) => {
                  clearError()
                  setForm((f) => ({ ...f, imageUrls: [...f.imageUrls, ...urls] }))
                }}
              />
            </div>
          </div>
        ) : null}

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <div className="mt-8 flex flex-col gap-3 border-t border-border pt-6 sm:mt-10 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex w-full items-center sm:w-auto">
            {step > 0 ? (
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => setStep((s) => s - 1)}
                disabled={mutationLoading}
              >
                Back
              </Button>
            ) : null}
          </div>
          <div
            className={`flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end sm:gap-3 ${step === 0 ? 'sm:ml-auto' : ''}`}
          >
            {step < LAST_STEP ? (
              <Button
                type="button"
                className="w-full sm:w-auto sm:min-w-[7.5rem]"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canGoNext() || mutationLoading}
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto"
                  disabled={mutationLoading || imageUploading || !canGoNext()}
                  onClick={() => submit(false)}
                >
                  {mutationLoading ? 'Saving…' : 'Save as draft'}
                </Button>
                <Button
                  type="button"
                  className="w-full sm:w-auto"
                  disabled={mutationLoading || imageUploading || !canGoNext()}
                  onClick={() => submit(true)}
                >
                  {mutationLoading ? 'Publishing…' : 'Publish to storefront'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
